/**
 * FeedbackForm
 *
 * Body of the add-feedback page (`FeedbackPage`): the top disclaimer, the
 * multiline 200-char feedback input, the editable screenshots picker grid, and
 * the "Done" submit CTA. Ports Flutter's `_contentWidget` column
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:285-509`):
 *
 *   - disclaimer   → `_titleWidget` (`:311-318`)
 *   - feedback text→ `_feedbackWidget` (`:321-362`; height 100, max 200,
 *                    hint "Your feedback...")
 *   - grid         → `_screenshotsGrid` (`:364-471`, extracted to
 *                    {@link FeedbackScreenshotsPickerGrid})
 *   - Done button  → `_submitButton` (`:473-509`; the web primary color
 *                    `theme.button.primary` when submittable, else the disabled
 *                    surface look)
 *
 * Co-located with `FeedbackPage` to keep the page under the ~200-line Should-Fix
 * threshold from `presentation.md`. Pure presentational: it owns no state and
 * opens no sheet — the page supplies the values + callbacks (`useFeedback` owns
 * the draft state; the page owns the sheet/navigation surfaces).
 *
 * UI conventions deviate from Flutter per `presentation.md`: the field surface
 * uses `theme.secondary` (the established web text-field background, matching
 * `AddRoadmapForm`) in place of Flutter's translucent `backgroundColorLight`,
 * and the enabled/disabled submit intent is carried via the web primary color
 * `theme.button.primary` (web owns its button styling; an accepted parity break
 * from Flutter's `AppColors.activeRed`, `:488`) / `theme.disabled`.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InputText } from '@/presentation/components/common/InputText';
import {
  ExpauseButton,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import { FeedbackScreenshotsPickerGrid } from '@/presentation/feature/feedback/add/components/FeedbackScreenshotsPickerGrid';
import type { LocalFeedbackScreenshot } from '@/presentation/feature/feedback/add/hooks/useFeedback';

export interface FeedbackFormProps {
  description: string;
  setDescription: (value: string) => void;
  screenshots: LocalFeedbackScreenshot[];
  canSubmit: boolean;
  onAddTap: () => void;
  onScreenshotTap: (index: number) => void;
  onRemoveTap: (index: number) => void;
  onDone: () => void;
}

export const FeedbackForm = ({
  description,
  setDescription,
  screenshots,
  canSubmit,
  onAddTap,
  onScreenshotTap,
  onRemoveTap,
  onDone,
}: FeedbackFormProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.spacing.lg,
      }}
    >
      {/* Top disclaimer (`:311-318`). */}
      <p
        style={{
          margin: 0,
          color: theme.text.secondary,
          fontSize: responsive.font.base,
        }}
      >
        {l10n.feedback.disclaimer}
      </p>

      {/* Feedback text field — max 200 chars (`:321-362`). */}
      <div
        style={{
          backgroundColor: theme.secondary,
          borderRadius: responsive.borderRadius.base,
          overflow: 'hidden',
        }}
      >
        <InputText
          qa={{ id: 'feedback-input' }}
          value={description}
          onChange={setDescription}
          maxLength={InputLengthFormatters.feedbackDescriptionMaxLength}
          placeholder={l10n.feedback.inputHint}
          multiline
          backgroundColor="transparent"
          height={responsive.form.textareaHeight}
          padding={responsive.spacing.lg}
          style={{ width: '100%', boxSizing: 'border-box' }}
        />
      </div>

      {/* Editable screenshots picker grid (`:364-471`). */}
      <FeedbackScreenshotsPickerGrid
        screenshots={screenshots}
        onAddTap={onAddTap}
        onScreenshotTap={onScreenshotTap}
        onRemoveTap={onRemoveTap}
      />

      {/* "Done" submit — web primary color when submittable, else disabled (`:473-509`). */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: responsive.spacing.lg,
        }}
      >
        <ExpauseButton
          {...qaAttr({
            id: 'feedback-done',
            value: canSubmit ? 'enabled' : 'disabled',
          })}
          variant={ButtonVariant.primary}
          backgroundColor={canSubmit ? theme.button.primary : theme.disabled}
          disabled={!canSubmit}
          onClick={onDone}
        >
          {l10n.feedback.done}
        </ExpauseButton>
      </div>
    </div>
  );
};
